import { useEffect, useRef } from 'preact/hooks';

import { areAirplayAdsEnabled } from '../../../utils/airplayAds';

import { useVideoElement } from './useVideoElement';
import { useTracker } from './useTracker';
import { useDuration } from './useDuration';
import { usePlayer } from './PlayerProvider';

declare global {
  interface Window {
    WebKitPlaybackTargetAvailabilityEvent?: string;
  }
  interface HTMLVideoElement {
    webkitShowPlaybackTargetPicker?: () => void;
    webkitCurrentPlaybackTargetIsWireless?: boolean;
  }
}

export function usePlayerAirplay() {
  const { trackAirplayStarted, trackAirplayEnded } = useTracker();
  const videoElement = useVideoElement();
  const { duration } = useDuration();
  const player = usePlayer();

  const durationRef = useRef(duration);

  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  const displayAirplay = Boolean(window.WebKitPlaybackTargetAvailabilityEvent);

  useEffect(() => {
    if (areAirplayAdsEnabled()) {
      videoElement?.removeAttribute('x-webkit-wirelessvideoplaybackdisabled');
    }
  }, []);

  useEffect(() => {
    if (!videoElement || !displayAirplay) {
      return;
    }

    const availabilityListener = () => {
      if (areAirplayAdsEnabled()) {
        videoElement.removeAttribute('x-webkit-wirelessvideoplaybackdisabled');
      }
    };

    const connectivityListener = () => {
      if (areAirplayAdsEnabled()) {
        videoElement.removeAttribute('x-webkit-wirelessvideoplaybackdisabled');
      }
      if (videoElement.webkitCurrentPlaybackTargetIsWireless) {
        trackAirplayStarted();

        player.trigger('cast', {
          available: true,
          active: true,
          deviceName: undefined,
          type: 'airPlay',
        });
      } else {
        trackAirplayEnded(durationRef.current);

        player.trigger('cast', {
          available: true,
          active: false,
          deviceName: undefined,
          type: 'airPlay',
        });
      }
    };

    videoElement.addEventListener(
      'webkitplaybacktargetavailabilitychanged',
      availabilityListener
    );

    videoElement.addEventListener(
      'webkitcurrentplaybacktargetiswirelesschanged',
      connectivityListener
    );

    return () => {
      videoElement.removeEventListener(
        'webkitplaybacktargetavailabilitychanged',
        availabilityListener
      );

      videoElement.removeEventListener(
        'webkitcurrentplaybacktargetiswirelesschanged',
        connectivityListener
      );
    };
  }, [
    displayAirplay,
    player,
    videoElement,
    trackAirplayStarted,
    trackAirplayEnded,
  ]);

  const handleAirplay = () => {
    videoElement?.webkitShowPlaybackTargetPicker?.();
  };

  return { displayAirplay, handleAirplay };
}
